/** @format */

import React, { useCallback, useState, useEffect, useRef } from "react";
import { useAppSelector } from "../../app/hooks";
import "./UserWidget.scss";
import { Button } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { ReactComponent as AccountIcon } from "bootstrap-icons/icons/person.svg";
import { ReactComponent as Cap } from "bootstrap-icons/icons/mortarboard.svg";
import { ReactComponent as ArrowDownLeft } from "bootstrap-icons/icons/arrow-down-left.svg";
import { ReactComponent as BoxArrowUpRight } from "bootstrap-icons/icons/box-arrow-up-right.svg";
import { ReactComponent as LifePreserver } from "bootstrap-icons/icons/life-preserver.svg";
import { ReactComponent as Person } from "bootstrap-icons/icons/person.svg";
import { ReactComponent as X } from "bootstrap-icons/icons/x.svg";
import { useTranslation } from "react-i18next";
import { useLocale } from "../i18n/hooks";
import UserAvatar from "./UserAvatar";
import FeatureFlag from "../featureFlag/FeatureFlag";
import { getRFBaseURL } from "../editable/GetRefLink";

const UserWidget: React.FC = () => {
  const data = useAppSelector((state) => state.auth.userInfo);

  const locale = useLocale();

  let guideURL;
  switch (data?.role) {
    case "wjec":
      guideURL =
        "http://resource.download.wjec.co.uk/vtc/editable-bl-docs/staff.html";
      break;
    case "teacher":
      guideURL =
        locale === "cy"
          ? "https://resource.download.wjec.co.uk/vtc/editable-bl-docs/athrawon.html"
          : "http://resource.download.wjec.co.uk/vtc/editable-bl-docs/teacher.html";
  }

  const [showUserOptions, setShowUserOptions] = useState(false);

  let userWidgetRef = useRef<HTMLDivElement>(null);

  const { t: _ } = useTranslation();
  const location = useLocation();

  const handleUserOptionsToggle = useCallback(() => {
    setShowUserOptions(!showUserOptions);
  }, [showUserOptions]);

  useEffect(() => {
    let handler = (e: any) => {
      if (userWidgetRef.current != null) {
        if (!userWidgetRef.current.contains(e.target)) {
          setShowUserOptions(false);
        }
      }
    };

    document.addEventListener("mousedown", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
    };
  });

  return (
    <>
      <div className="user-widget ms-0 ms-sm-3">
        <Button
          title="login"
          className="user-options-button"
          onClick={handleUserOptionsToggle}
        >
          {data !== undefined ? (
            <UserAvatar user={data} />
          ) : (
            // <span className="text-white">{_("login")}</span>
            <Person className="person-icon" title={_("login")} />
          )}
        </Button>
      </div>
      {showUserOptions && (
        <div
          className="bg-white border-rounded rounded-3 p-3 user-options-wrapper text-end"
          ref={userWidgetRef}
        >
          <div className="text-dark user-dropdown">
            {data !== undefined ? (
              <>
                <div className="user-details item d-flex align-items-center">
                  <UserAvatar user={data} />
                  <p className="m-0">{data?.meta.name}</p>
                  <X
                    className="close ms-auto"
                    viewBox="4 4 8 8"
                    onClick={handleUserOptionsToggle}
                  />
                </div>
                <div className="user-options pt-3" role="menu">
                  <>
                    <a
                      target="_blank"
                      rel="noreferrer noopener"
                      href={`${getRFBaseURL(window.location.hostname).url}/user/dashboard`}
                      role="menuitem"
                    >
                      <Cap fill="#AEAEAE" title="resources-icon" />
                      {data.role === "teacher"
                        ? _("My Resources")
                        : _("My Work")}
                      <BoxArrowUpRight
                        fill="#AEAEAE"
                        className="ms-auto me-0 p-2"
                      />
                    </a>
                    <a
                      target="_blank"
                      rel="noreferrer noopener"
                      href={`${getRFBaseURL(window.location.hostname).url}/user/account`}
                      role="menuitem"
                    >
                      <AccountIcon fill="#AEAEAE" title="account-icon" />
                      {_("My Account")}
                      <BoxArrowUpRight
                        fill="#AEAEAE"
                        className="ms-auto me-0 p-2"
                      />
                    </a>
                  </>
                  {guideURL && (
                    <a
                      target="_blank"
                      rel="noreferrer noopener"
                      href={guideURL}
                      role="menuitem"
                    >
                      <LifePreserver fill="#AEAEAE" title="help-icon" />
                      {_("Help & support")}
                      <BoxArrowUpRight
                        fill="#AEAEAE"
                        className="ms-auto me-0 p-2"
                      />
                    </a>
                  )}
                  <Link
                    to={`/auth/logout/`}
                    state={{ from: location }}
                    replace
                    role="menuitem"
                  >
                    <ArrowDownLeft fill="#AEAEAE" title="logout-icon" />
                    {_("Log Out")}
                  </Link>
                </div>
              </>
            ) : (
              <>
                <X
                  className="close ms-auto"
                  viewBox="4 4 8 8"
                  onClick={handleUserOptionsToggle}
                />
                <div className="user-options" role="menu">
                  <FeatureFlag
                    flag={"hwb"}
                    fallback={
                      <Link to="#" className="btn btn-link disabled">
                        <img
                          src="https://cdn.hwb.gov.wales/hwb-cms/4.34.31/img/hwb-logo.svg"
                          alt="Hwb"
                        />
                        {_("Login with Hwb")}
                      </Link>
                    }
                  >
                    <Link
                      to="/auth/hwblogin"
                      state={{ test: location.pathname }}
                      replace
                      title="hwb-link"
                    >
                      <img
                        src="https://cdn.hwb.gov.wales/hwb-cms/4.34.31/img/hwb-logo.svg"
                        alt="Hwb"
                      />
                      {_("Login with Hwb")}
                    </Link>
                  </FeatureFlag>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default UserWidget;
